import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as api from './inclusions-exclusions.api';

import type {
  CreateInclusionExclusionRequest,
  UpdateInclusionExclusionRequest,
  InclusionExclusionListQuery,
} from '@utazzo/shared';

const QK = {
  list: (query: InclusionExclusionListQuery) => ['inclusions-exclusions', 'list', query] as const,
  detail: (id: string) => ['inclusions-exclusions', 'detail', id] as const,
};

export const useInclusionsExclusionsList = (query: InclusionExclusionListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => api.listInclusionsExclusions(query),
    staleTime: 30_000,
  });

export const useCreateInclusionExclusion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateInclusionExclusionRequest) => api.createInclusionExclusion(payload),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['inclusions-exclusions', 'list'] });
      toast.success(`Added ${t.title}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add entry'),
  });
};

export const useUpdateInclusionExclusion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateInclusionExclusionRequest }) =>
      api.updateInclusionExclusion(id, payload),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ['inclusions-exclusions', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(t.id) });
      toast.success('Entry updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update entry'),
  });
};

export const useDeleteInclusionExclusion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteInclusionExclusion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inclusions-exclusions', 'list'] });
      toast.success('Entry deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete entry'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
